import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, PiggyBank, TrendingUp, Calculator, Cake, X, Landmark, Scale, Receipt, Fuel, Coins, Percent } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const allCalculators = [
  {
    category: 'Loans',
    items: [
      { title: 'EMI Calculator', url: '/', icon: PiggyBank, description: 'Calculate monthly loan payments' },
    ],
  },
  {
    category: 'Investments',
    items: [
      { title: 'SIP Calculator', url: '/sip-calculator', icon: TrendingUp, description: 'Plan systematic investments' },
      { title: 'FD Calculator', url: '/fd-calculator', icon: Landmark, description: 'Calculate fixed deposit returns' },
      { title: 'Compound Interest', url: '/compound-interest-calculator', icon: Coins, description: 'Calculate compound growth' },
    ],
  },
  {
    category: 'Utilities',
    items: [
      { title: 'General Calculator', url: '/calculator', icon: Calculator, description: 'Quick math operations' },
      { title: 'GST Calculator', url: '/gst-calculator', icon: Receipt, description: 'Calculate GST, CGST, SGST & IGST' },
      { title: 'Percentage Calculator', url: '/percentage-calculator', icon: Percent, description: 'Calculate percent increase & discount' },
      { title: 'Mileage Calculator', url: '/mileage-calculator', icon: Fuel, description: 'Calculate fuel efficiency & trip cost' },
    ],
  },
  {
    category: 'Others',
    items: [
      { title: 'Age Calculator', url: '/age-calculator', icon: Cake, description: 'Calculate age & birthday countdown' },
      { title: 'BMI Calculator', url: '/bmi-calculator', icon: Scale, description: 'Calculate body mass index' },
    ],
  },
];

interface AddCalculatorDialogProps {
  collapsed?: boolean;
}

export function AddCalculatorDialog({ collapsed = false }: AddCalculatorDialogProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const filteredCalculators = useMemo(() => {
    if (!searchQuery.trim()) return allCalculators;

    const query = searchQuery.toLowerCase();
    return allCalculators
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query)
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [searchQuery]);

  const handleSelect = (url: string) => {
    navigate(url);
    setOpen(false);
    setSearchQuery('');
  };

  const hasResults = filteredCalculators.some((cat) => cat.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            {collapsed ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Add calculator"
                className="h-9 w-9 mx-auto rounded-xl bg-primary/10 hover:bg-primary/20 text-primary"
              >
                <Plus className="h-5 w-5" />
              </Button>
            ) : (
              <Button
                variant="outline"
                aria-label="Add calculator"
                className="w-full justify-start gap-2 rounded-xl border-primary/20 bg-primary/5 hover:bg-primary/10 text-primary"
              >
                <Plus className="h-4 w-4" />
                Add Calculator
              </Button>
            )}
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Add Calculator</TooltipContent>
      </Tooltip>

      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Plus className="h-5 w-5 text-primary" />
            Calculator Launcher
          </DialogTitle>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search calculators..."
            aria-label="Search calculators"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-11"
            autoFocus
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Clear search input"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Calculator Grid */}
        <div className="overflow-y-auto max-h-[55vh] pr-2 -mr-2">
          <AnimatePresence mode="wait">
            {hasResults ? (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {filteredCalculators.map((category) => (
                  <div key={category.category}>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">
                      {category.category}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {category.items.map((item) => (
                        <Link
                          key={item.url}
                          to={item.url}
                          onClick={() => {
                            setOpen(false);
                            setSearchQuery('');
                          }}
                          className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-muted/30 hover:bg-primary/10 hover:ring-2 hover:ring-primary/20 transition-all text-center group"
                        >
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <item.icon className="h-7 w-7" />
                          </div>
                          <div className="space-y-1">
                            <p className="font-medium text-sm text-foreground">{item.title}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16 text-center"
              >
                <Search className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No calculators found.</p>
                <p className="text-sm text-muted-foreground/70">Try another search term.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
